/*
 * --------------------------------------------------------------------
 * File: \src\utils\WebSocketContext.js
 * Created: Monday, 7th October 2025
 * Author: System Consultancy Services Sdn. Bhd.
 * 
 * WebSocket Context - Provides WebSocket service to all components
 * --------------------------------------------------------------------
 */

import React, { createContext, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import websocketService from './WebSocketService';
import {
  setConnecting,
  setConnected,
  setDisconnected,
  setError,
  addMessage,
  incrementReconnectAttempts,
} from '../app/websocketSlice';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children, url, autoConnect = false, options = {} }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Set up event handlers
    const unsubscribeOpen = websocketService.onOpen(() => {
      dispatch(setConnected());
    });

    const unsubscribeClose = websocketService.onClose(() => {
      dispatch(setDisconnected());
      dispatch(incrementReconnectAttempts());
    });

    const unsubscribeError = (error) => {
      dispatch(setError(error.message || 'WebSocket error occurred'));
    };
    websocketService.onError(unsubscribeError);

    const unsubscribeMessage = websocketService.onMessage((data) => {
      dispatch(addMessage(data));
    });

    // Auto-connect if url is provided and autoConnect is true
    if (url && autoConnect) {
      dispatch(setConnecting());
      websocketService.connect(url, options);
    }

    // Cleanup on unmount
    return () => {
      unsubscribeOpen();
      unsubscribeClose();
      unsubscribeMessage();
      websocketService.disconnect();
    };
  }, [url, autoConnect, options, dispatch]);

  return (
    <WebSocketContext.Provider value={websocketService}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;
